import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Search, MapPin } from 'lucide-react';
import { agentAppAPI } from '../../services/api';
import toast from 'react-hot-toast';

const fmt = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

// Quick-access customer picker for the Balance Sheet bottom tab — tapping a row goes
// straight to that customer's ledger, skipping the full profile page.
export default function AgentLedgerListPage() {
  const { setHeader } = useOutletContext();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setHeader({ title: 'Balance Sheet', showBack: false });
  }, [setHeader]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await agentAppAPI.getCustomers(search ? { search } : {});
      setCustomers(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  // Same "society" grouping as the main customer list — and the same house-number sort
  // within each group, see [[project_customers_area_sort_bug]] / AgentCustomerListPage.js.
  const groupedBySociety = useMemo(() => {
    const map = new Map();
    for (const c of customers) {
      const key = c.address?.area || 'Unassigned';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    // "4-F" sequences with "4" (same unit, not a separate one) — strip the suffix before
    // the numeric compare below runs.
    const houseNo = (c) => (c.catv?.houseNumber || c.broadband?.houseNumber || '').replace(/-F$/i, '').trim();
    for (const list of map.values()) {
      list.sort((a, b) => {
        const ha = houseNo(a), hb = houseNo(b);
        if (!ha && !hb) return 0;
        if (!ha) return 1;
        if (!hb) return -1;
        return ha.localeCompare(hb, undefined, { numeric: true, sensitivity: 'base' });
      });
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [customers]);

  return (
    <div>
      <div style={{ padding: '10px 12px' }}>
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-light)' }} />
          <input
            className="form-control"
            placeholder="Search name / mobile / code"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: 32 }}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading…</div>
      ) : customers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No customers found</div>
      ) : (
        groupedBySociety.map(([society, list]) => (
          <div key={society}>
            <div style={{
              padding: '7px 14px', fontSize: '.72rem', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '.03em', color: 'var(--text-muted)', background: 'var(--bg-page, #f3f4f6)',
              borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)',
              position: 'sticky', top: 0, zIndex: 5
            }}>
              {society} <span style={{ fontWeight: 500, textTransform: 'none' }}>({list.length})</span>
            </div>
            {list.map((c) => {
              const fullAddress = [c.address?.line1, c.address?.landmark].filter(Boolean).join(', ');
              return (
                <div
                  key={c._id}
                  onClick={() => navigate(`/agent/customer/${c._id}/ledger`)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                    borderBottom: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer'
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '.9rem' }}>{c.name}</div>
                    {fullAddress && (
                      <div style={{
                        fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 1,
                        display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                      }}>
                        <MapPin size={10} style={{ flexShrink: 0 }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{fullAddress}</span>
                      </div>
                    )}
                  </div>
                  <div style={{
                    background: (c.outstandingBalance || 0) > 0 ? '#dc2626' : 'var(--bg-sidebar)', color: '#fff',
                    borderRadius: 16, padding: '4px 10px', fontSize: '.75rem', fontWeight: 700, flexShrink: 0
                  }}>
                    ₹{fmt(c.outstandingBalance)}
                  </div>
                </div>
              );
            })}
          </div>
        ))
      )}
    </div>
  );
}
